import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import QRCode from "react-qr-code"

import { Button } from "components/Button"
import FatButton from "components/Button/FatButton/FatButton"
import { Buttons, Card } from "components/Card/Card"
import Loader from "components/Loaders/Loader/Loader"
import { openNotification } from "components/Message/Message"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { logout } from "containers/Login/services/actions"
import surveyActions from "containers/Survey/services/actions"
import { cdn } from "core/cdn"
import { SURVEY_PAGE } from "core/constants"
import { useLanguage } from "locales"

import styles from "./PreventionHandoff.module.scss"
import actions from "./services/actions"
import { HANDOFF_CANCELLED, HANDOFF_COMPLETED, HANDOFF_EXPIRED, HANDOFF_OPENED, HANDOFF_POLL_INTERVAL_MS } from "./services/constants"
import { getPreventionHandoffLink, getPreventionHandoffStatus, isPreventionHandoffLoading } from "./services/selector"

// Renew the link before the API expiry so the patient never scans a dead QR code
const LINK_REFRESH_MARGIN_MS = 60 * 1000
const MIN_LINK_REFRESH_DELAY_MS = 30 * 1000

enum Screen {
  Choice,
  QrCode,
  Phone,
}

const PreventionHandoff = (): JSX.Element => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const link = useSelector(getPreventionHandoffLink)
  const status = useSelector(getPreventionHandoffStatus)
  const loading = useSelector(isPreventionHandoffLoading)
  const [screen, setScreen] = useState<Screen>(Screen.Choice)
  // A link was just requested from the choice screen: show the QR code as soon as it arrives
  const [awaitingLink, setAwaitingLink] = useState(false)
  // The QR code is shown again for a handoff the phone already opened (tab closed, link lost):
  // no automatic switch to the phone screen, the patient leaves through the link under the QR code
  const [reshownOpened, setReshownOpened] = useState(false)
  const [interruptModal, setInterruptModal] = useState(false)

  // RETOUR on the choice screen: back to the pharmacist confirmation (previous() skips redirect steps)
  const backToPrevious = () => {
    dispatch(actions.cancel())
    dispatch(surveyActions.previous())
    navigate(SURVEY_PAGE, { replace: true })
  }

  // RETOUR on the QR screen, and "Continuer sur la borne" once the phone took over: the patient
  // is asked again where to answer (ticket acceptance criteria). The handoff stays alive so a
  // change of mind brings back the same link, with the answers already given on the phone.
  const backToChoice = () => setScreen(Screen.Choice)

  const liveLink =
    !!link && status !== HANDOFF_EXPIRED && status !== HANDOFF_CANCELLED && status !== HANDOFF_COMPLETED

  // "Sur la borne" on the choice screen. The handoff step is a redirect question: `next` shows the
  // presentation slides, then the patient choice, on the kiosk.
  const startOnKiosk = () => {
    dispatch(actions.cancel())
    dispatch(surveyActions.next())
    navigate(SURVEY_PAGE, { replace: true })
  }

  // A questionnaire already open on the phone is interrupted for good: the patient confirms first
  const continueOnKiosk = () => {
    if (liveLink && status === HANDOFF_OPENED) setInterruptModal(true)
    else startOnKiosk()
  }

  const choosePhone = () => {
    if (liveLink) {
      setReshownOpened(status === HANDOFF_OPENED)
      setScreen(Screen.QrCode)
      // The API is the truth on a handoff kept alive off-screen: a dead one is replaced right away
      dispatch(actions.fetchStatus())
      return
    }
    setAwaitingLink(true)
    dispatch(actions.create())
  }

  useEffect(() => {
    if (awaitingLink && link) {
      setAwaitingLink(false)
      setReshownOpened(false)
      setScreen(Screen.QrCode)
    }
  }, [awaitingLink, link])

  // Poll the handoff while the QR code is displayed ("opened": the phone took over), and as long as it is alive
  // on the other screens: once the checkup is validated on the phone, the kiosk logs out (product rule)
  useEffect(() => {
    if (!link || (screen !== Screen.QrCode && !liveLink)) return
    const interval = setInterval(() => dispatch(actions.fetchStatus()), HANDOFF_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [screen, link, liveLink, dispatch])

  useEffect(() => {
    if (status === HANDOFF_COMPLETED) dispatch(logout())
  }, [status, dispatch])

  useEffect(() => {
    if (screen !== Screen.QrCode) return
    if (status === HANDOFF_OPENED && !reshownOpened) setScreen(Screen.Phone)
    if (status === HANDOFF_EXPIRED) {
      setReshownOpened(false)
      dispatch(actions.create())
    }
  }, [screen, status, reshownOpened, dispatch])

  // Same lifetime computation as the photomaton QR code (Upload.tsx). Never once the phone took
  // over: the API then keeps the handoff for the completion window, and a new one would cancel it
  useEffect(() => {
    if (screen !== Screen.QrCode || !link || status === HANDOFF_OPENED) return
    const expiresAt = new Date(link.end_at).getTime()
    const issuedAt = new Date(link.created_at).getTime()
    if (Number.isNaN(expiresAt)) return
    const lifetime = Number.isNaN(issuedAt) ? expiresAt - Date.now() : expiresAt - issuedAt
    const elapsed = Date.now() - link.received_at
    const delay = Math.max(lifetime - elapsed - LINK_REFRESH_MARGIN_MS, MIN_LINK_REFRESH_DELAY_MS)
    const timeout = setTimeout(() => dispatch(actions.create()), delay)
    return () => clearTimeout(timeout)
  }, [screen, link, status, dispatch])

  const sendSms = () => {
    dispatch(actions.sendSms())
    openNotification({ type: "info", text: languages.uploadDocumentSmsSentNotification })
  }

  if (loading) return <Loader />

  if (screen === Screen.Phone) {
    return (
      <>
        <PreviousButton onClick={backToChoice} />
        <Card title={languages.preventionHandoffPhoneTitle} titleSize="medium" subtitle={languages.preventionHandoffPhoneText}>
          <img className={styles.PhoneIllustration} src={cdn("/images/prevention/handoff_phone.svg")} alt="" />
          <Buttons direction="vertical" align="center">
            <Button type="primary" danger wide="long" onClick={() => dispatch(logout())}>
              {languages.preventionHandoffLogout}
            </Button>
            <div className={styles.ContinueOnKiosk} onClick={backToChoice}>
              {languages.preventionHandoffContinueOnKiosk}
            </div>
          </Buttons>
        </Card>
      </>
    )
  }

  if (screen === Screen.QrCode && link) {
    return (
      <>
        <PreviousButton onClick={backToChoice} />
        <Card title={languages.preventionHandoffQrTitle} titleSize="medium" subtitle={languages.preventionHandoffQrSubtitle_html}>
          <div className={styles.QrCode}>
            <QRCode size={256} style={{ height: "auto", width: "100%" }} value={link.url} viewBox="0 0 256 256" />
          </div>
          <div className={styles.CannotScan} onClick={sendSms}>
            {languages.UDCannotScan}
          </div>
          {reshownOpened && (
            <div className={styles.CannotScan} onClick={() => setScreen(Screen.Phone)}>
              {languages.preventionHandoffContinueOnPhone}
            </div>
          )}
        </Card>
      </>
    )
  }

  return (
    <>
      <PreviousButton onClick={backToPrevious} />
      <Card title={languages.preventionHandoffChoiceTitle} titleSize="medium">
        <div className={styles.Choices}>
          <FatButton type="primary" img={cdn("/images/prevention/handoff_phone.svg")} onClick={choosePhone} doNotFormat>
            <span>{languages.preventionHandoffOnPhone}</span>
          </FatButton>
          <FatButton img={cdn("/images/prevention/handoff_kiosk.svg")} onClick={continueOnKiosk} doNotFormat>
            <span>{languages.preventionHandoffOnKiosk}</span>
          </FatButton>
        </div>
        <GenericModal
          visible={interruptModal}
          title={languages.preventionHandoffInterruptTitle}
          contentText={languages.preventionHandoffInterruptText}
          bodyStyle={{ textAlign: "center" }}
          acceptText={languages.preventionHandoffContinueOnKiosk}
          onAccept={startOnKiosk}
          onCancel={() => setInterruptModal(false)}
        />
      </Card>
    </>
  )
}

export default PreventionHandoff
